if (!customElements.get('hero-video-early-bird')) {
  customElements.define('hero-video-early-bird', class HeroVideoEarlyBird extends HTMLElement {
    connectedCallback() {
      this.previewVideo = this.querySelector('.hero-video-early-bird__preview-video');
      this.modal = this.querySelector('[data-video-modal]');
      this.modalVideo = this.querySelector('.hero-video-early-bird__modal-video');
      this.openButton = this.querySelector('[data-open-video]');
      this.closeButton = this.querySelector('[data-close-video]');
      this.form = this.querySelector('[data-early-bird-form]');
      this.button = this.querySelector('[data-early-bird-submit]');
      this.error = this.querySelector('[data-early-bird-error]');
      this.mainVariantInput = this.querySelector('[data-main-variant]');
      this.gifts = this.querySelector('[data-early-bird-gifts]');
      this.showGiftsVariantIds = new Set(
        (this.dataset.showGiftsVariantIds || '').replace(/，/g, ',').split(',').map((id) => id.trim()).filter(Boolean)
      );
      this.price = this.querySelector('[data-current-price]');
      this.comparePrice = this.querySelector('[data-compare-price]');
      this.comparePriceWrap = this.querySelector('[data-compare-price-wrap]');
      this.quantityInput = this.querySelector('[data-early-bird-quantity]');
      this.quantityDecreaseButton = this.querySelector('[data-quantity-decrease]');
      this.quantityIncreaseButton = this.querySelector('[data-quantity-increase]');
      this.variantInputs = [...this.querySelectorAll('[data-variant-option-input]')];
      this.variants = this.readJSON('[data-early-bird-variants]') || [];
      this.abortController = new AbortController();

      this.openButton?.addEventListener('click', () => this.openVideo());
      this.closeButton?.addEventListener('click', () => this.closeVideo());
      this.modal?.addEventListener('close', () => this.resetModalVideo());
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeVideo();
      });
      document.addEventListener('fullscreenchange', () => {
        if (!document.fullscreenElement && this.modal?.open) this.modal.close();
      }, { signal: this.abortController.signal });
      this.modalVideo?.addEventListener('webkitendfullscreen', () => {
        if (this.modal?.open) this.modal.close();
        else this.resetModalVideo();
      });
      this.form?.addEventListener('submit', (event) => this.addBundle(event));
      this.bindQuantityControls();
      this.bindVariantPicker();
      this.bindVariantUpdates();
      this.updateGiftVisibility(this.mainVariantInput?.value);
      window.addEventListener('pageshow', () => {
        requestAnimationFrame(() => {
          this.restoreVariantSelection();
          this.restoreQuantity();
        });
      }, { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      this.abortController.abort();
      if (this.unsubscribeVariant) this.unsubscribeVariant();
    }

    async openVideo() {
      if (!this.modal || !this.modalVideo) return;
      this.modal.showModal();
      this.previewVideo?.pause();
      this.modalVideo.muted = false;
      this.modalVideo.currentTime = 0;
      this.modalVideo.play().catch(() => {});

      try {
        if (this.modal.requestFullscreen) {
          await this.modal.requestFullscreen();
        } else if (this.modalVideo.webkitEnterFullscreen) {
          this.modalVideo.webkitEnterFullscreen();
        }
      } catch (_) {
        // The dialog remains as a functional fallback when fullscreen is blocked.
      }
    }

    async closeVideo() {
      if (document.fullscreenElement) {
        await document.exitFullscreen().catch(() => {});
      }
      if (this.modal?.open) this.modal.close();
      else this.resetModalVideo();
    }

    resetModalVideo() {
      if (!this.modalVideo) return;
      this.modalVideo.pause();
      this.modalVideo.currentTime = 0;
      this.previewVideo?.play().catch(() => {});
    }

    readJSON(selector) {
      try { return JSON.parse(this.querySelector(selector)?.textContent || 'null'); }
      catch (_) { return null; }
    }

    bindVariantPicker() {
      if (!this.variantInputs.length || !this.variants.length) return;
      this.variantInputs.forEach((input) => {
        input.addEventListener('change', () => this.selectVariantFromOptions(), {
          signal: this.abortController.signal,
        });
      });
      this.updateSelectedOptionLabels();
      this.updateOptionAvailability();
    }

    selectVariantFromOptions() {
      const selectedOptions = this.getSelectedOptions();
      const variant = this.variants.find((item) => (
        item.options?.every((value, index) => value === selectedOptions[index])
      ));

      this.updateSelectedOptionLabels();
      this.updateOptionAvailability();
      this.applyVariant(variant);
      if (variant) this.publishVariant(variant);
    }

    restoreVariantSelection() {
      if (!this.variantInputs.length || !this.variants.length) return;
      const urlVariantId = this.dataset.syncCurrentProduct === 'true'
        ? new URLSearchParams(window.location.search).get('variant')
        : null;
      const urlVariant = this.variants.find((item) => String(item.id) === urlVariantId);
      const selectedOptions = this.getSelectedOptions();
      const selectedVariant = this.variants.find((item) => (
        item.options?.every((value, index) => value === selectedOptions[index])
      ));
      const variant = urlVariant || selectedVariant;
      if (!variant) return;

      const previousId = String(this.mainVariantInput?.value || '');
      this.selectVariant(variant.id);
      if (String(variant.id) !== previousId) this.publishVariant(variant);
    }

    getSelectedOptions() {
      const selectedOptions = [];
      this.variantInputs.forEach((input) => {
        if (input.checked) selectedOptions[Number(input.dataset.optionIndex)] = input.value;
      });
      return selectedOptions;
    }

    updateSelectedOptionLabels() {
      this.querySelectorAll('[data-variant-option]').forEach((group) => {
        const selected = group.querySelector('[data-variant-option-input]:checked');
        const output = group.querySelector('[data-selected-option-value]');
        if (output && selected) output.textContent = selected.value;
      });
    }

    updateOptionAvailability() {
      const selectedOptions = this.getSelectedOptions();
      this.variantInputs.forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);
        const available = this.variants.some((variant) => (
          variant.available && variant.options?.every((value, index) => (
            value === (index === optionIndex ? input.value : selectedOptions[index])
          ))
        ));
        input.disabled = !available && !input.checked;
        const label = input.nextElementSibling;
        label?.classList.toggle('is-unavailable', !available);
        if (label) {
          if (available) label.removeAttribute('aria-disabled');
          else label.setAttribute('aria-disabled', 'true');
        }
      });
    }

    applyVariant(variant) {
      if (this.mainVariantInput) this.mainVariantInput.value = variant?.id || '';
      this.updateGiftVisibility(variant?.id);
      if (this.price && variant?.price_formatted) this.price.textContent = variant.price_formatted;
      if (this.comparePriceWrap) {
        const hasComparePrice = Boolean(variant?.compare_at_price_formatted);
        this.comparePriceWrap.hidden = !hasComparePrice;
        if (this.comparePrice) this.comparePrice.textContent = variant?.compare_at_price_formatted || '';
      }
      if (this.button) this.button.disabled = !variant?.available;
      if (variant?.available && this.error) this.error.hidden = true;
    }

    selectVariant(id) {
      const variant = this.variants.find((item) => String(item.id) === String(id));
      if (!variant) {
        if (this.mainVariantInput) this.mainVariantInput.value = id;
        this.updateGiftVisibility(id);
        return;
      }
      variant.options?.forEach((value, index) => {
        const input = this.variantInputs.find((item) => (
          Number(item.dataset.optionIndex) === index && item.value === value
        ));
        if (input) input.checked = true;
      });
      this.updateSelectedOptionLabels();
      this.updateOptionAvailability();
      this.applyVariant(variant);
    }

    updateGiftVisibility(variantId) {
      if (this.gifts) this.gifts.hidden = !this.showGiftsVariantIds.has(String(variantId || ''));
    }

    publishVariant(variant) {
      if (this.dataset.syncCurrentProduct === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
      window.dispatchEvent(new CustomEvent('east-oak:product-variant-change', {
        detail: { variant, source: this },
      }));
      if (
        this.dataset.syncCurrentProduct === 'true' &&
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.variantChange
      ) {
        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.dataset.sectionId, variant, source: this },
        });
      }
    }

    bindVariantUpdates() {
      if (this.dataset.syncCurrentProduct !== 'true') return;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.source === this) return;
          if (event?.data?.variant?.id) this.selectVariant(event.data.variant.id);
        });
      }
      document.addEventListener('change', (event) => {
        const input = event.target.closest('product-info input[name="id"]');
        if (input?.value) this.selectVariant(input.value);
      }, { signal: this.abortController.signal });
    }

    bindQuantityControls() {
      this.quantityDecreaseButton?.addEventListener('click', () => {
        this.setQuantity(this.getQuantity() - 1);
      }, { signal: this.abortController.signal });
      this.quantityIncreaseButton?.addEventListener('click', () => {
        this.setQuantity(this.getQuantity() + 1);
      }, { signal: this.abortController.signal });
      this.quantityInput?.addEventListener('change', () => {
        this.setQuantity(this.quantityInput.value);
      }, { signal: this.abortController.signal });
      window.addEventListener('east-oak:product-quantity-change', (event) => {
        if (event.detail?.source === this) return;
        this.setQuantity(event.detail?.quantity, { silent: true });
      }, { signal: this.abortController.signal });

      const syncedQuantity = Number.parseInt(window.EastOakSyncedProductQuantity, 10);
      this.setQuantity(Number.isFinite(syncedQuantity) ? syncedQuantity : this.getQuantity(), { silent: true });
    }

    getQuantity() {
      const value = Number.parseInt(this.quantityInput?.value, 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    restoreQuantity() {
      const quantity = this.getQuantity();
      this.setQuantity(quantity, { silent: quantity === this.currentQuantity });
    }

    setQuantity(value, options = {}) {
      if (!this.quantityInput) return;
      const quantity = Math.max(1, Number.parseInt(value, 10) || 1);
      this.currentQuantity = quantity;
      this.quantityInput.value = quantity;
      if (this.quantityDecreaseButton) this.quantityDecreaseButton.disabled = quantity <= 1;
      if (!options.silent) this.publishQuantity(quantity);
    }

    publishQuantity(quantity) {
      window.EastOakSyncedProductQuantity = quantity;
      window.dispatchEvent(new CustomEvent('east-oak:product-quantity-change', {
        detail: { quantity, source: this },
      }));
    }

    async addBundle(event) {
      event.preventDefault();
      if (!this.form || this.button?.disabled) return;
      const mainId = Number(this.mainVariantInput?.value);
      const quantity = this.getQuantity();
      const items = [];
      if (mainId) items.push({ id: mainId, quantity });
      if (!mainId) return this.showError('Please select a main product.');

      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.error.hidden = true;
      try {
        const response = await fetch(this.form.action + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this offer.');
        window.location.assign(this.form.dataset.cartUrl);
      } catch (error) {
        this.showError(error.message);
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }
  });
}
